import { useCallback, useEffect, useRef, useState } from 'react';
import { notesService } from '../adapters/notes';
import toast from '../lib/toast';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function useNoteChat(noteId: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(async () => {
    if (!input.trim() || loading || !noteId) return;
    const msg = input.trim();
    setMessages(prev => [
      ...prev,
      { role: 'user', content: msg },
      { role: 'assistant', content: '' },
    ]);
    setInput('');
    setLoading(true);
    try {
      const reply = await notesService.chatNote(noteId, msg);
      setMessages(prev => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: reply };
        return copy;
      });
    } catch {
      toast.error('Chat failed');
      setMessages(prev => prev.slice(0, -2));
    } finally {
      setLoading(false);
    }
  }, [noteId, input, loading]);

  const clearChat = useCallback(() => {
    if (!window.confirm('Clear chat history?')) return;
    setMessages([]);
  }, []);

  return { messages, input, setInput, send, loading, clearChat, scrollRef };
}
